import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import { useTranslation } from 'react-i18next';
import {
  fetchAdminSummary,
  generateAdminTestData,
  resetAdminTestData,
  simulateAdminNotification,
} from '../api/admin';
import { useToast } from '../components/tavern/ToastProvider';

const ToolCard = ({
  action,
  confirmLabel,
  copy,
  disabled,
  icon: Icon,
  loading,
  onRun,
  title,
  tone = 'primary',
}) => (
  <Card variant="outlined">
    <CardContent>
      <Stack spacing={2} alignItems="flex-start">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: tone === 'danger' ? 'rgba(168, 58, 50, 0.1)' : 'rgba(15, 76, 69, 0.1)',
            color: tone === 'danger' ? 'error.main' : 'primary.main',
          }}
        >
          <Icon />
        </Box>
        <Box>
          <Typography variant="h3">{title}</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {copy}
          </Typography>
        </Box>
        <Button
          variant={tone === 'danger' ? 'outlined' : 'contained'}
          color={tone === 'danger' ? 'error' : 'primary'}
          disabled={disabled || loading}
          onClick={onRun}
          startIcon={loading ? <CircularProgress color="inherit" size={18} /> : null}
        >
          {loading ? confirmLabel : action}
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

const TavernMasterToolsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingTool, setWorkingTool] = useState('');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      setSummary(await fetchAdminSummary());
    } catch (err) {
      setError(err.response?.status === 403 ? t('adminTools.forbidden') : t('adminTools.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const runTool = async (toolKey, action, successKey) => {
    setWorkingTool(toolKey);
    setError('');

    try {
      const result = await action();
      setSummary((current) => ({
        ...(current || {}),
        generated: result.generated || current?.generated,
      }));
      toast.success(t(successKey));
    } catch (err) {
      const message = t('adminTools.actionError');
      setError(message);
      toast.error(message);
    } finally {
      setWorkingTool('');
    }
  };

  const handleReset = () => {
    const confirmed = window.confirm(t('adminTools.reset.confirm'));
    if (!confirmed) return;

    runTool('reset', resetAdminTestData, 'adminTools.reset.success');
  };

  const generated = summary?.generated || { games: 0, nights: 0, notices: 0 };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress color="secondary" />
      </Stack>
    );
  }

  if (error && !summary) {
    return (
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={2} alignItems="flex-start">
            <SecurityOutlinedIcon color="error" />
            <Typography variant="h2">{t('adminTools.forbiddenTitle')}</Typography>
            <Typography color="text.secondary">{error}</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={4} sx={{ py: { xs: 2, md: 3 } }}>
      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('adminTools.eyebrow')}
        </Typography>
        <Typography component="h1" variant="h1">
          {t('adminTools.title')}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 760 }}>
          {t('adminTools.subtitle')}
        </Typography>
      </Box>

      <Alert severity="info" icon={<SecurityOutlinedIcon />}>
        {t('adminTools.safety')}
      </Alert>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={t('adminTools.generated.games', { count: generated.games })} />
        <Chip label={t('adminTools.generated.nights', { count: generated.nights })} />
        <Chip label={t('adminTools.generated.notices', { count: generated.notices })} />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <ToolCard
          icon={AutoAwesomeOutlinedIcon}
          title={t('adminTools.generate.title')}
          copy={t('adminTools.generate.copy')}
          action={t('adminTools.generate.action')}
          confirmLabel={t('adminTools.generate.running')}
          loading={workingTool === 'generate'}
          disabled={Boolean(workingTool)}
          onRun={() =>
            runTool('generate', generateAdminTestData, 'adminTools.generate.success')
          }
        />
        <ToolCard
          icon={NotificationsActiveOutlinedIcon}
          title={t('adminTools.notify.title')}
          copy={t('adminTools.notify.copy')}
          action={t('adminTools.notify.action')}
          confirmLabel={t('adminTools.notify.running')}
          loading={workingTool === 'notify'}
          disabled={Boolean(workingTool)}
          onRun={() =>
            runTool('notify', simulateAdminNotification, 'adminTools.notify.success')
          }
        />
        <ToolCard
          icon={DeleteSweepOutlinedIcon}
          title={t('adminTools.reset.title')}
          copy={t('adminTools.reset.copy')}
          action={t('adminTools.reset.action')}
          confirmLabel={t('adminTools.reset.running')}
          loading={workingTool === 'reset'}
          disabled={Boolean(workingTool)}
          onRun={handleReset}
          tone="danger"
        />
      </Box>
    </Stack>
  );
};

export default TavernMasterToolsPage;
